import React, {
  ChangeEventHandler,
  memo,
  useState,
  KeyboardEventHandler,
  FC,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import { ChevronLeftIconButton, ChevronRightIconButton } from './Icons';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { PaginationProps } from './types';

const KEYCODE = {
  ENTER: 13,
};

const useTextFieldStyles = makeStyles(theme => ({
  root: {
    width: theme.spacing(4),
    '& > div': {
      width: theme.spacing(4),
      height: theme.spacing(3),
    },
  },
  input: {
    textAlign: 'center',
    '&::-webkit-clear-button, &::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      /* display: none; <- Crashes Chrome on hover */
      WebkitAppearance: 'none',
      /* <-- Apparently some margin are still there even though it's hidden */
      margin: 0,
      MozAppearance: 'textfield',
    },
  },
}));

const Pagination: FC<PaginationProps> = ({
  pageIndex,
  totalPages,
  setPageIndex,
  prevDataQa,
  nextDataQa,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [innerPageIndex, setInnerPageIndex] = useState<string>('1');

  useEffect(() => {
    setInnerPageIndex((pageIndex + 1).toString());
  }, [pageIndex]);

  useEffect(() => {
    if (totalPages <= 0) {
      setPageIndex(-1);
    } else if (totalPages - 1 < pageIndex) {
      setPageIndex(0);
    }
  }, [totalPages]);

  const prev = useCallback(() => {
    if (pageIndex > 0) {
      setInnerPageIndex(pageIndex.toString());
      setPageIndex(pageIndex - 1);
    }
  }, [pageIndex, setPageIndex, setInnerPageIndex]);

  const next = useCallback(() => {
    if (pageIndex < totalPages - 1) {
      setInnerPageIndex((pageIndex + 2).toString());
      setPageIndex(pageIndex + 1);
    }
  }, [pageIndex, totalPages, setPageIndex, setInnerPageIndex]);

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      const isNumber = event.target.value.match('^[0-9]*$');
      const pageIndexFromEvent = parseInt(event.target.value, 10);

      if (
        pageIndexFromEvent >= 1 &&
        pageIndexFromEvent <= totalPages &&
        isNumber &&
        Number.isInteger(pageIndexFromEvent) &&
        totalPages > 0
      ) {
        setInnerPageIndex(event.target.value);
      } else if (!pageIndexFromEvent) {
        setInnerPageIndex('');
      }
    },
    [totalPages, setInnerPageIndex]
  );

  const onBlur = useCallback(() => {
    const isNumber = innerPageIndex.match('^[0-9]*$');

    if (innerPageIndex && isNumber) {
      setPageIndex(parseInt(innerPageIndex, 10) - 1);
    } else {
      setInnerPageIndex((pageIndex + 1).toString());
    }
  }, [innerPageIndex, setPageIndex, setInnerPageIndex]);

  const onKeyUp: KeyboardEventHandler = useCallback(
    event => {
      if (event.keyCode === KEYCODE.ENTER) {
        onBlur();
        inputRef.current && inputRef.current.blur();
      }
    },
    [onBlur, inputRef]
  );

  const hasPrev = pageIndex > 0;
  const hasNext = pageIndex < totalPages - 1;

  const styles = useTextFieldStyles();

  return (
    <Box display="inline-flex" alignItems="center" color="grey.500">
      <ChevronLeftIconButton
        disabled={!hasPrev}
        onClick={prev}
        tabIndex={hasPrev ? 0 : undefined}
        aria-disabled={!hasPrev}
        size="small"
        data-qa={prevDataQa}
      />
      <Box
        display="flex"
        alignItems="center"
        data-page={pageIndex + 1}
        data-total-pages={totalPages}
      >
        <TextField
          classes={{
            root: styles.root,
          }}
          InputProps={{
            classes: {
              input: styles.input,
            },
          }}
          inputRef={inputRef}
          type="text"
          value={innerPageIndex}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onBlur={onBlur}
          variant="outlined"
        />
        <Box pr={0.5} pl={1}>
          <Typography variant="body1">／</Typography>
        </Box>
        <Typography variant="body1">{totalPages}</Typography>
      </Box>
      <ChevronRightIconButton
        disabled={!hasNext}
        onClick={next}
        tabIndex={hasNext ? 0 : undefined}
        aria-disabled={!hasNext}
        size="small"
        data-qa={nextDataQa}
      />
    </Box>
  );
};

export default memo(Pagination);
